import { useUserContext } from '@lemonade-hq/nuiza';
import { MenuOptionType, useUniclientLayoutContext } from '@lemonade-hq/uniclient-app';
import type { MenuOption } from '@lemonade-hq/uniclient-app';
import type {
  HeaderAction as HeaderActionType,
  HeaderMenuProps,
  MenuSubActions,
  UniclientAction,
  UniclientFrame,
  UniclientHeaderActions,
} from '@lemonade-hq/uniclient-chat';
import { PredefinedMenuOptionType } from '@lemonade-hq/uniclient-chat';
import { isNativeApp, NativePresentationMode } from '@lemonade-hq/uniclient-mobile-bridge';
import type { IconName } from '@lemonade-hq/uniclient-theme';
import { useMemo } from 'react';

export interface PredefinedHeaderAction extends Pick<MenuOption, 'title'> {
  readonly icon?: IconName;
  readonly visible?: boolean;
  readonly disabled?: boolean;
  readonly onAction?: () => Promise<void> | void;
}

export interface PredefinedHeaderActionSection extends Pick<MenuOption, 'title'> {
  readonly title: string;
  readonly actions: PredefinedHeaderAction[];
}

export type HeaderMenuActionsProps = Record<MenuOptionType, Omit<PredefinedHeaderAction, 'title'>>;

export type HeaderPredefinedMenuActions = Partial<Record<PredefinedMenuOptionType, PredefinedHeaderAction | undefined>>;

export interface WizardMenuProps {
  readonly predefinedActions?: HeaderPredefinedMenuActions;
  readonly subActions: MenuSubActions;
  readonly performAction: (action?: UniclientAction | undefined) => Promise<unknown>;
}

export const useWizardMenuPredefinedActions = (
  actionsProps: HeaderMenuActionsProps,
  redirectFn: (url: string) => void,
): HeaderPredefinedMenuActions => {
  const { menu } = useUniclientLayoutContext();
  return useMemo(() => {
    return Object.keys(MenuOptionType).reduce<HeaderPredefinedMenuActions>((acc, key) => {
      const type = key as MenuOptionType;

      const menuItem = menu[type];
      if (actionsProps[type].visible === false || menuItem == null) {
        return acc;
      }

      acc[type] = {
        title: menuItem.title,
        ...actionsProps[type],
        onAction: async () => {
          const url = menuItem.url;

          if (url != null) {
            redirectFn(url.href);
            return;
          }

          const { onAction } = actionsProps[type];
          return await onAction?.();
        },
      };
      return acc;
    }, {} as HeaderPredefinedMenuActions);
  }, [menu, actionsProps, redirectFn]);
};

export function shouldShowNativeAction<TAction extends HeaderActionType | HeaderMenuProps | PredefinedMenuOptionType>(
  action?: TAction | null,
): action is TAction {
  if (action == null) return false;

  const isRoot = window.mobileSettings?.presentationMode === NativePresentationMode.Root;
  const isNativeOnlyAction =
    typeof action === 'object' && 'nativeMobileOnly' in action && action.nativeMobileOnly === true;

  return !isNativeOnlyAction || (isNativeApp() && !isRoot);
}

export function shouldShowLoggedInAction<TAction extends HeaderActionType | HeaderMenuProps | PredefinedMenuOptionType>(
  action?: TAction,
  isLogged = false,
): boolean {
  if (action == null) return false;

  const isLoggedInOnlyAction =
    typeof action === 'object' && 'onlyWhenLoggedIn' in action && action.onlyWhenLoggedIn === true;

  return !isLoggedInOnlyAction || isLogged;
}

export const useFilteredWizardHeaderActions = <
  TActions extends (HeaderActionType | HeaderMenuProps | PredefinedMenuOptionType.FreeTextInput)[] | MenuSubActions,
>(
  actions: TActions,
): TActions => {
  const { user } = useUserContext();
  const isLogged = user != null;

  return useMemo(() => {
    return actions.filter(action => {
      if (action === PredefinedMenuOptionType.FreeTextInput) {
        return true;
      }

      if (typeof action === 'object' && 'title' in action && 'actions' in action) {
        const filteredSubActions = action.actions.filter(subAction => {
          return shouldShowNativeAction(subAction) && shouldShowLoggedInAction(subAction, isLogged);
        });
        return filteredSubActions.length > 0;
      }

      return shouldShowNativeAction(action) && shouldShowLoggedInAction(action, isLogged);
    }) as TActions;
  }, [actions, isLogged]);
};

export interface UseWizardHeaderActionsParams {
  readonly currentStepFrame?: UniclientFrame | null;
  readonly defaultHeaderActions: UniclientHeaderActions;
}

export const useWizardHeaderActions = ({
  currentStepFrame,
  defaultHeaderActions,
}: UseWizardHeaderActionsParams): (HeaderActionType | HeaderMenuProps | PredefinedMenuOptionType.FreeTextInput)[] => {
  const headerActionsWithFreeText: (HeaderActionType | HeaderMenuProps | PredefinedMenuOptionType.FreeTextInput)[] =
    useMemo(() => {
      const rawHeaderActions: (HeaderActionType | HeaderMenuProps)[] =
        currentStepFrame != null
          ? currentStepFrame.header?.actions?.right != null
            ? currentStepFrame.header.actions.right
            : [...(defaultHeaderActions.right ?? [])]
          : [];

      const shouldShowFreeTextInputButton = currentStepFrame?.freeTextInput === 'toggle';

      if (shouldShowFreeTextInputButton) {
        return [PredefinedMenuOptionType.FreeTextInput, ...rawHeaderActions];
      }

      return rawHeaderActions;
    }, [currentStepFrame, defaultHeaderActions.right]);

  return useFilteredWizardHeaderActions(headerActionsWithFreeText);
};

export const useWizardMenu = ({
  predefinedActions,
  subActions,
  performAction,
}: WizardMenuProps): (PredefinedHeaderAction | PredefinedHeaderActionSection)[] => {
  const filteredSubActions = useFilteredWizardHeaderActions(subActions);

  return useMemo(() => {
    return filteredSubActions.reduce<(PredefinedHeaderAction | PredefinedHeaderActionSection)[]>(
      (acc, action) => {
        if (typeof action === 'string') {
          const predefinedAction = predefinedActions?.[action];
          if (predefinedAction) {
            acc.push(predefinedAction);
          }
        } else if ('title' in action && 'actions' in action) {
          const sectionActions: PredefinedHeaderAction[] = [];
          action.actions.forEach(subAction => {
            if (typeof subAction === 'string') {
              const predefinedAction = predefinedActions?.[subAction];
              if (predefinedAction) {
                sectionActions.push(predefinedAction);
              }
            } else if (subAction.content != null) {
              sectionActions.push({
                title: subAction.content,
                icon: subAction.icon,
                disabled: subAction.disabled,
                onAction: async () => {
                  await performAction(subAction);
                },
              });
            }
          });

          if (sectionActions.length > 0) {
            acc.push({
              title: action.title,
              actions: sectionActions,
            });
          }
        } else if (action.content != null) {
          acc.push({
            title: action.content,
            icon: action.icon,
            disabled: action.disabled,
            onAction: async () => {
              await performAction(action);
            },
          });
        }

        return acc;
      },
      [] as (PredefinedHeaderAction | PredefinedHeaderActionSection)[],
    );
  }, [filteredSubActions, predefinedActions, performAction]);
};
